import React, { ReactElement } from 'react';
import { ChildrenOrNull, PoolChip, PoolTypography } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { Locale } from '@synergy/models';
import { Stack, styled } from '@mui/material';
import { getLocalizedString } from '@synergy/utils';

import { Contact } from '../../models/contact';
import { ContactDetailsNoData } from './contact-details-no-data';

const StyledLink = styled('a')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    textDecoration: 'none',
    color: theme.palette.text.primary,

    '&:hover, &:focus': {
        color: theme.palette.primary.main,
    },
    '& > label': {
        cursor: 'pointer',
    },
}));

export interface ContactDetailsContentProps {
    contactDetail: Contact | null;
    isLoading: boolean;
    locales: Locale[];
}

export const ContactDetailsContent = (props: ContactDetailsContentProps): ReactElement | null => {
    const { contactDetail, isLoading, locales } = props;

    if (isLoading) {
        return null;
    }

    const noData = !contactDetail?.positionName && !contactDetail?.emails && !contactDetail?.phones;

    if (noData) {
        return <ContactDetailsNoData />;
    }

    const renderPhone = (type: string, value: string) => (
        <PoolTypography key={`${value}-${type}`}>
            <StyledLink href={`tel:${value.replace(/[^0-9+]/g, '')}`}>
                <label>
                    <FormattedMessage id={`app.contacts.${type}`} />
                </label>
                {value}
            </StyledLink>
        </PoolTypography>
    );

    const renderEmail = (type: string, value: string) => (
        <PoolTypography key={`${value}-${type}`}>
            <StyledLink href={`mailto:${value}`}>
                <label>
                    <FormattedMessage id={`app.contacts.${type}`} />
                </label>
                {value}
            </StyledLink>
        </PoolTypography>
    );

    const renderTopic = (type: string, value: string) => (
        <PoolTypography key={`${value}-${type}`}>
            <PoolChip
                type="tag"
                label={value}
                sx={{ flexShrink: 0, width: 'auto', minWidth: 'unset', textAlign: 'center' }}
            />
        </PoolTypography>
    );

    return (
        <>
            <ChildrenOrNull value={contactDetail?.positionName}>
                {positionName => (
                    <Stack>
                        <label>
                            <FormattedMessage id="app.header.position" />
                        </label>
                        {positionName}
                    </Stack>
                )}
            </ChildrenOrNull>
            <ChildrenOrNull value={contactDetail?.phone}>{phone => renderPhone('phone.default', phone)}</ChildrenOrNull>
            <ChildrenOrNull value={contactDetail?.phones}>
                {phones => phones.map(phone => renderPhone(phone.type, phone.number))}
            </ChildrenOrNull>
            <ChildrenOrNull value={contactDetail?.email}>{email => renderEmail('email.default', email)}</ChildrenOrNull>
            <ChildrenOrNull value={contactDetail?.emails}>
                {emails => emails.map(email => renderEmail(email.type, email.email))}
            </ChildrenOrNull>
            <ChildrenOrNull value={contactDetail?.topics}>
                {topics => (
                    <>
                        <label>
                            <FormattedMessage id={'app.configuration.topics'} />
                        </label>
                        {topics.map(topic => renderTopic('topic', getLocalizedString(locales, topic.title)))}
                    </>
                )}
            </ChildrenOrNull>
        </>
    );
};
